import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { AuthCard, FormError, errorMessage } from "../components/AuthCard";

export const MIN_PASSWORD = 8;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function SignupPage() {
  const { user, loading, signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setError("Enter a valid email address.");
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters for your password.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signup(name, email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(errorMessage(err, "We couldn't create your account. Try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Create your account"
      subtitle="Start learning straight away. We'll email you a link to confirm your address."
      onSubmit={onSubmit}
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <label>
        <span>
          Name <span className="optional">(optional)</span>
        </span>
        <input type="text" autoComplete="name" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>
      <label>
        Email
        <input
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <label>
        Password
        <input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-describedby="password-hint"
        />
      </label>
      <span id="password-hint" className="field-hint field-hint-below">
        At least {MIN_PASSWORD} characters.
      </span>
      <FormError message={error} />
      <button className="btn btn-primary btn-block" disabled={busy}>
        {busy ? "Creating account…" : "Create account"}
      </button>
    </AuthCard>
  );
}
